import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Scale, ArrowLeft, FileText, Calendar, Download, RefreshCw, FolderInput, UserCheck, Plus, X, Pencil, Settings } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/App";
import { toast } from "sonner";
import EventosList from "@/components/EventosList";
import EventoForm from "@/components/EventoForm";
import DocumentosList from "@/components/DocumentosList";
import DocumentoUpload from "@/components/DocumentoUpload";
import EventosTimeline from "@/components/EventosTimeline";
import ValorizacionPanel from "@/components/ValorizacionPanel";
import FasesProcesales from "@/components/FasesProcesales";
import TopNav from "@/components/TopNav";

const fmtCLP = (v) => new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(v || 0);
const formatDate = (d) => { try { return new Date(d).toLocaleDateString("es-CL"); } catch { return d; } };

const TRIBUNAL_LABELS = {
  juzgado_civil: "Juzgado Civil", juzgado_letras: "Juzgado de Letras", juzgado_laboral: "Juzgado de Letras del Trabajo",
  juzgado_familia: "Juzgado de Familia", juzgado_garantia: "Juzgado de Garantía", juzgado_oral_penal: "Tribunal Oral en lo Penal",
  corte_apelaciones: "Corte de Apelaciones", corte_suprema: "Corte Suprema", juzgado_policia_local: "Juzgado de Policía Local", tribunal_arbitral: "Tribunal Arbitral"
};
const ESTADO_PROCESAL_LABELS = {
  presentada: "Presentada", admitida: "Admitida", notificada: "Notificada", contestacion: "En contestación",
  conciliacion: "En conciliación", prueba: "Periodo de prueba", sentencia: "En sentencia", apelacion: "En apelación",
  ejecutoriada: "Ejecutoriada", cumplimiento: "En cumplimiento", archivada: "Archivada"
};

const InfoField = ({ label, value, highlight }) => (
  <div>
    <p className="text-[10px] uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>{label}</p>
    <p className={`text-xs font-medium ${highlight ? "font-bold" : ""}`} style={{ color: highlight ? "var(--primary)" : "var(--text-secondary)" }}>{value}</p>
  </div>
);

function CasoDetalle({ user, onLogout }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [caso, setCaso] = useState(null);
  const [eventos, setEventos] = useState([]);
  const [documentos, setDocumentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("timeline");
  const [selectedEvento, setSelectedEvento] = useState(null);
  const [showEventoForm, setShowEventoForm] = useState(false);
  const [showDocumentoUpload, setShowDocumentoUpload] = useState(false);
  const [fases, setFases] = useState([]);
  
  // Estados para importar hechos
  const [showImportarModal, setShowImportarModal] = useState(false);
  const [casosDisponibles, setCasosDisponibles] = useState([]);
  const [casoOrigenId, setCasoOrigenId] = useState("");
  const [hechosOrigen, setHechosOrigen] = useState([]);
  const [hechosSeleccionados, setHechosSeleccionados] = useState([]);
  const [renumerar, setRenumerar] = useState(true);
  const [importando, setImportando] = useState(false);
  
  // Estados para eliminar evento con clave
  const [eventoToDelete, setEventoToDelete] = useState(null);
  const [deleteEventoPassword, setDeleteEventoPassword] = useState("");
  
  // Estados para editar abogados
  const [editingAbogados, setEditingAbogados] = useState(false);
  const [abogadosEdit, setAbogadosEdit] = useState([]);
  const [abogadosDir, setAbogadosDir] = useState([]);
  
  // Estados para editar caso completo
  const [showEditCaso, setShowEditCaso] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editEmpresa, setEditEmpresa] = useState({});
  const [editDemandado, setEditDemandado] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);

  const isCEO = user?.actuacion === "CEO";

  const fmtInputCLP = (v) => { const n = parseInt(String(v).replace(/\D/g, "")) || 0; return n.toLocaleString("es-CL"); };
  const parseNumCLP = (v) => parseInt(String(v).replace(/\D/g, "")) || 0;

  const openEditCaso = () => {
    setEditForm({
      titulo_caso: caso.titulo_caso || "",
      tipo_demanda: caso.tipo_demanda || "civil",
      tipo_conflicto: caso.tipo_conflicto || "",
      descripcion_breve: caso.descripcion_breve || "",
      estado_caso: caso.estado_caso || "en_preparacion",
      rit_rol: caso.rit_rol || "",
      tribunal: caso.tribunal || "",
      numero_juzgado: caso.numero_juzgado || "",
      materia: caso.materia || "",
      cuantia_demanda: caso.cuantia_demanda || 0,
      estado_procesal: caso.estado_procesal || "",
      fecha_demanda: caso.fecha_demanda || "",
      fecha_notificacion: caso.fecha_notificacion || "",
      fecha_audiencia_proxima: caso.fecha_audiencia_proxima || "",
      domicilio_demandado: caso.domicilio_demandado || "",
      representante_legal_demandado: caso.representante_legal_demandado || "",
      observaciones: caso.observaciones || "",
      valorizacion: caso.valorizacion || { monto_principal: 0, costo_inversion: 0, lucro_cesante: 0, dano_moral: 0, reajuste_ipc: 0, costas_procesales: 0 }
    });
    setEditEmpresa({
      id: caso.empresa_cliente?.id || caso.empresa_cliente_id || "",
      nombre_legal: caso.empresa_cliente?.nombre_legal || "",
      rut: caso.empresa_cliente?.rut || "",
      tipo_persona: caso.empresa_cliente?.tipo_persona || "juridica",
      contacto_principal_nombre: caso.empresa_cliente?.contacto_principal_nombre || "",
      contacto_principal_email: caso.empresa_cliente?.contacto_principal_email || "",
      contacto_principal_telefono: caso.empresa_cliente?.contacto_principal_telefono || "",
    });
    setEditDemandado({
      id: caso.demandado?.id || caso.demandado_id || "",
      nombre_legal: caso.demandado?.nombre_legal || "",
      rut: caso.demandado?.rut || "",
      tipo_persona: caso.demandado?.tipo_persona || "juridica",
    });
    setShowEditCaso(true);
  };

  const saveEditCaso = async () => {
    setSavingEdit(true);
    try {
      // Si es CEO, actualizar también empresa demandante y demandado
      if (isCEO) {
        if (editEmpresa.id && editEmpresa.nombre_legal) {
          await api.put(`/admin/empresas-clientes/${editEmpresa.id}`, {
            nombre_legal: editEmpresa.nombre_legal,
            rut: editEmpresa.rut || "",
            tipo_persona: editEmpresa.tipo_persona || "juridica",
            contacto_principal_nombre: editEmpresa.contacto_principal_nombre || "",
            contacto_principal_email: editEmpresa.contacto_principal_email || "",
            contacto_principal_telefono: editEmpresa.contacto_principal_telefono || "",
          });
        }
        if (editDemandado.id && editDemandado.nombre_legal) {
          await api.put(`/admin/demandados/${editDemandado.id}`, {
            nombre_legal: editDemandado.nombre_legal,
            rut: editDemandado.rut || "",
            tipo_persona: editDemandado.tipo_persona || "juridica",
          });
        }
      }
      await api.put(`/casos/${id}`, editForm);
      toast.success("Caso actualizado");
      setShowEditCaso(false);
      loadCaso();
    } catch (err) { toast.error(err.response?.data?.detail || "Error actualizando"); }
    finally { setSavingEdit(false); }
  };

  const ef = (field, value) => setEditForm({ ...editForm, [field]: value });
  const efVal = (key, value) => setEditForm({ ...editForm, valorizacion: { ...editForm.valorizacion, [key]: parseNumCLP(value) } });
  const efEmp = (field, value) => setEditEmpresa({ ...editEmpresa, [field]: value });
  const efDem = (field, value) => setEditDemandado({ ...editDemandado, [field]: value });

  // Export PDF del caso
  const [exportingCaso, setExportingCaso] = useState(false);
  const handleExportCasoPdf = async () => {
    setExportingCaso(true);
    toast.info("Generando PDF con análisis IA, puede tardar 30-60 segundos…");
    try {
      const res = await api.get(`/casos/${id}/exportar/pdf`, { responseType: "blob", timeout: 180000 });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      const safe = (caso.titulo_caso || "caso").replace(/[^a-zA-Z0-9_-]/g, "_").slice(0, 50);
      link.download = `Caso_${caso.numero_caso || "?"}_${safe}.pdf`;
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF descargado");
    } catch (err) { toast.error("Error generando PDF"); }
    finally { setExportingCaso(false); }
  };

  useEffect(() => {
    loadCaso();
  }, [id]);

  const loadCaso = async () => {
    try {
      const [casoRes, eventosRes, documentosRes, fasesRes, estadoRes] = await Promise.all([
        api.get(`/casos/${id}`),
        api.get(`/casos/${id}/eventos`),
        api.get(`/casos/${id}/documentos`),
        api.get(`/casos/${id}/fases`),
        api.get(`/casos/${id}/documentos/estado-archivos`).catch(() => ({ data: { documentos: [], perdidos: 0 } }))
      ]);

      setCaso(casoRes.data);
      setEventos(eventosRes.data);
      const estadoMap = {};
      estadoRes.data.documentos.forEach(e => { estadoMap[e.documento_id] = e.archivo_existe; });
      setDocumentos(documentosRes.data.map(d => ({ ...d, archivo_existe: estadoMap[d.id] !== false })));
      if (estadoRes.data.perdidos > 0) {
        toast.warning(`${estadoRes.data.perdidos} documento(s) no tienen su archivo físico. Márcalos con "Archivo perdido" y resúbelos.`, { duration: 8000 });
      }
      setFases(fasesRes.data);
    } catch (error) {
      toast.error('Error cargando caso');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveEvento = async (eventoData, files = []) => {
    try {
      let eventoId;
      
      // Guardar el evento primero
      if (selectedEvento) {
        await api.put(`/casos/${id}/eventos/${selectedEvento.id}`, eventoData);
        eventoId = selectedEvento.id;
        toast.success('Evento actualizado');
      } else {
        const response = await api.post(`/casos/${id}/eventos`, eventoData);
        eventoId = response.data.id;
        toast.success('Evento creado');
      }
      
      // Subir los documentos si hay
      if (files && files.length > 0) {
        toast.info(`Subiendo ${files.length} documento(s)...`);
        
        for (const file of files) {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('tipo_documento', file.type.includes('pdf') ? 'otro' : 'otro');
          formData.append('fecha_documento', eventoData.fecha_evento);
          formData.append('descripcion_breve', `Documento adjunto a ${eventoData.titulo_evento}`);
          formData.append('evento_hecho_id', eventoId);
          
          await api.post(`/casos/${id}/documentos`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        }
        
        toast.success(`${files.length} documento(s) subido(s) y vinculado(s)`);
      }
      
      await loadCaso();
      setShowEventoForm(false);
      setSelectedEvento(null);
    } catch (error) {
      toast.error('Error guardando evento o documentos');
      console.error(error);
    }
  };

  const handleDeleteEvento = async (eventoId) => {
    // Abrir modal de confirmación con clave
    setEventoToDelete(eventoId);
  };

  const confirmDeleteEvento = async () => {
    if (!deleteEventoPassword) {
      toast.error('Ingresa la clave de seguridad');
      return;
    }

    try {
      await api.delete(`/casos/${id}/eventos/${eventoToDelete}?password=${deleteEventoPassword}`);
      toast.success('Hecho movido a la papelera');
      setEventoToDelete(null);
      setDeleteEventoPassword("");
      await loadCaso();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Clave incorrecta');
      } else {
        toast.error('Error eliminando evento');
      }
      console.error(error);
    }
  };

  const handleDocumentoUploaded = async () => {
    await loadCaso();
    setShowDocumentoUpload(false);
  };

  // Funciones de exportación movidas al Dashboard

  const handleExportarTimeline = async () => {
    try {
      const response = await api.get(`/casos/${id}/exportar-timeline`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `timeline_caso_${id}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Timeline exportada a Word');
    } catch (error) {
      toast.error('Error exportando timeline');
      console.error(error);
    }
  };

  const handleDescargarTodosDocumentos = async () => {
    if (documentos.length === 0) {
      toast.error('No hay documentos para descargar');
      return;
    }

    try {
      toast.info('Preparando descarga...');
      const response = await api.get(`/casos/${id}/documentos/descargar-todos`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `documentos_caso_${id}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`${documentos.length} documentos descargados en ZIP`);
    } catch (error) {
      toast.error('Error descargando documentos');
      console.error(error);
    }
  };

  const handleAbrirImportarModal = async () => {
    try {
      // Cargar todos los casos excepto el actual
      const response = await api.get('/casos');
      const otrosCasos = response.data.filter(c => c.id !== id);
      setCasosDisponibles(otrosCasos);
      setShowImportarModal(true);
    } catch (error) {
      toast.error('Error cargando casos disponibles');
      console.error(error);
    }
  };

  const handleSeleccionarCasoOrigen = async (casoId) => {
    setCasoOrigenId(casoId);
    setHechosSeleccionados([]);
    
    try {
      // Cargar hechos del caso seleccionado
      const response = await api.get(`/casos/${casoId}/eventos`);
      setHechosOrigen(response.data);
    } catch (error) {
      toast.error('Error cargando hechos del caso');
      console.error(error);
    }
  };

  const handleToggleHecho = (hechoId) => {
    setHechosSeleccionados(prev => {
      if (prev.includes(hechoId)) {
        return prev.filter(id => id !== hechoId);
      } else {
        return [...prev, hechoId];
      }
    });
  };

  const handleImportarHechos = async () => {
    if (!casoOrigenId || hechosSeleccionados.length === 0) {
      toast.error('Selecciona al menos un hecho para importar');
      return;
    }

    try {
      setImportando(true);
      const response = await api.post(`/casos/${id}/importar-hechos`, {
        caso_origen_id: casoOrigenId,
        evento_ids: hechosSeleccionados,
        renumerar: renumerar
      });

      toast.success(response.data.message);
      
      // Cerrar modal y recargar
      setShowImportarModal(false);
      setCasoOrigenId("");
      setHechosOrigen([]);
      setHechosSeleccionados([]);
      await loadCaso();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error importando hechos');
      console.error(error);
    } finally {
      setImportando(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900"></div>
      </div>
    );
  }

  if (!caso) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-gray-600 mb-4">Caso no encontrado</p>
          <Button onClick={() => navigate('/dashboard')}>Volver</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }} data-testid="case-detail-page">
      <TopNav user={user} onLogout={onLogout || (() => { localStorage.removeItem('token'); window.location.href='/'; })} />

      <main className="container mx-auto px-6 py-6 max-w-7xl">
        {/* Breadcrumb */}
        <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 text-xs mb-4 transition-colors" style={{ color: "var(--text-muted)" }} data-testid="back-to-dashboard-btn">
          <ArrowLeft className="h-3.5 w-3.5" />
          <span className="hover:underline">Dashboard</span>
          <span>/</span>
          <span style={{ color: "var(--text-secondary)" }}>{caso.titulo_caso}</span>
        </button>

        {/* Info Card */}
        <div className="ls-card p-5 mb-5" data-testid="case-info-card">
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                {caso.numero_caso && <span className="text-xs font-bold px-2 py-0.5 rounded-md" style={{ background: "var(--primary-light)", color: "var(--primary)" }} data-testid="case-number-badge">#{caso.numero_caso}</span>}
                <h1 className="text-lg font-bold" style={{ color: "var(--text)" }}>{caso.titulo_caso}</h1>
                {caso.tipo_demanda && <span className="ls-badge" style={{ background: "var(--accent-blue-light)", color: "var(--accent-blue)" }}>{caso.tipo_demanda}</span>}
              </div>
              <p className="text-sm" style={{ color: "var(--text-secondary)" }}><span style={{ color: "var(--primary)" }}>Demandante:</span> {caso.empresa_cliente?.nombre_legal} <span style={{ color: "var(--text-muted)" }}>vs</span> <span style={{ color: "var(--accent-rose)" }}>Demandado:</span> {caso.demandado?.nombre_legal}</p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="flex items-center gap-2">
                <button onClick={openEditCaso} className="p-1.5 rounded-lg transition-colors hover:bg-slate-100" title="Editar caso" data-testid="edit-caso-btn">
                  <Settings className="h-4 w-4" style={{ color: "var(--text-muted)" }} />
                </button>
                <span className={`ls-badge ${caso.estado_caso === "presentado" ? "ls-badge-done" : "ls-badge-pending"}`}>
                  {caso.estado_caso?.replace("_", " ")}
                </span>
              </div>
              <Button onClick={handleExportCasoPdf} disabled={exportingCaso} size="sm" className="h-7 text-[11px] gap-1 ls-btn-primary" data-testid="export-caso-pdf-btn">
                {exportingCaso ? <><RefreshCw className="h-3 w-3 animate-spin" />Generando…</> : <><FileText className="h-3 w-3" />Exportar caso a PDF</>}
              </Button>
            </div>
          </div>
          <p className="text-sm mb-3" style={{ color: "var(--text-secondary)" }}>{caso.descripcion_breve}</p>
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs" style={{ color: "var(--text-muted)" }}>
            <span>Conflicto: <b style={{ color: "var(--text-secondary)" }}>{caso.tipo_conflicto}</b></span>
            <span>Creado: <b style={{ color: "var(--text-secondary)" }}>{new Date(caso.fecha_creacion).toLocaleDateString("es-CL")}</b></span>
          </div>
          {caso.abogados_asignados?.length > 0 || editingAbogados ? (
            <div className="mt-3 pt-3" style={{ borderTop: "1px solid var(--border-light)" }}>
              {editingAbogados ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-medium" style={{ color: "var(--text-muted)" }}>Editar Abogados</span>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" className="h-6 text-[10px] px-2" onClick={() => setEditingAbogados(false)}>Cancelar</Button>
                      <Button size="sm" className="h-6 text-[10px] px-2 ls-btn-primary" data-testid="save-abogados-btn" onClick={async () => {
                        try {
                          await api.put(`/casos/${id}/abogados`, { abogados_asignados: abogadosEdit.filter(a => a.nombre || a.email) });
                          toast.success("Abogados actualizados");
                          setEditingAbogados(false);
                          loadCaso();
                        } catch { toast.error("Error actualizando"); }
                      }}>Guardar</Button>
                    </div>
                  </div>
                  {/* Directorio quick-add */}
                  {abogadosDir.length > 0 && abogadosEdit.length < 3 && (
                    <div className="flex flex-wrap gap-1">
                      {abogadosDir.filter(ad => !abogadosEdit.some(a => a.email === ad.email)).map(ad => (
                        <button key={ad.id} type="button" onClick={() => setAbogadosEdit([...abogadosEdit, { nombre: ad.nombre, email: ad.email }])}
                          className="text-[10px] px-2 py-1 rounded-md border transition-all hover:border-teal-300 hover:bg-teal-50 flex items-center gap-1"
                          style={{ borderColor: "var(--border)", color: "var(--text-secondary)" }}>
                          <Plus className="h-2.5 w-2.5" />{ad.nombre}
                        </button>
                      ))}
                    </div>
                  )}
                  {abogadosEdit.map((ab, i) => (
                    <div key={i} className="flex gap-2 items-center">
                      <Input className="h-7 text-xs flex-1" value={ab.nombre} onChange={(e) => { const u = [...abogadosEdit]; u[i] = { ...u[i], nombre: e.target.value }; setAbogadosEdit(u); }} placeholder="Nombre" />
                      <Input className="h-7 text-xs flex-1" value={ab.email} onChange={(e) => { const u = [...abogadosEdit]; u[i] = { ...u[i], email: e.target.value }; setAbogadosEdit(u); }} placeholder="Email" />
                      <button onClick={() => setAbogadosEdit(abogadosEdit.filter((_, idx) => idx !== i))} className="text-red-400 hover:text-red-600"><X className="h-3.5 w-3.5" /></button>
                    </div>
                  ))}
                  {abogadosEdit.length < 3 && (
                    <button onClick={() => setAbogadosEdit([...abogadosEdit, { nombre: "", email: "" }])} className="text-[10px] flex items-center gap-1" style={{ color: "var(--primary)" }}>
                      <Plus className="h-3 w-3" /> Agregar manual
                    </button>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="text-[11px]" style={{ color: "var(--text-muted)" }}>Abogados:</span>
                  {caso.abogados_asignados?.map((ab, i) => (
                    <span key={i} className="text-[11px] px-2 py-0.5 rounded-md font-medium" style={{ background: "var(--primary-light)", color: "var(--primary-dark)" }}>{ab.nombre || ab.email}</span>
                  ))}
                  <button
                    onClick={async () => {
                      setAbogadosEdit(caso.abogados_asignados || []);
                      try { const res = await api.get("/abogados"); setAbogadosDir(res.data); } catch {}
                      setEditingAbogados(true);
                    }}
                    className="p-1 rounded hover:bg-slate-100 transition-colors ml-1"
                    data-testid="edit-abogados-btn"
                  >
                    <Pencil className="h-3 w-3" style={{ color: "var(--text-muted)" }} />
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="mt-3 pt-3" style={{ borderTop: "1px solid var(--border-light)" }}>
              <button
                onClick={async () => {
                  setAbogadosEdit([{ nombre: "", email: "" }]);
                  try { const res = await api.get("/abogados"); setAbogadosDir(res.data); } catch {}
                  setEditingAbogados(true);
                }}
                className="text-[11px] flex items-center gap-1 transition-colors"
                style={{ color: "var(--primary)" }}
                data-testid="add-abogados-btn"
              >
                <UserCheck className="h-3.5 w-3.5" /> Asignar abogados
              </button>
            </div>
          )}
        </div>

        {/* Datos del Tribunal + Fechas */}
        {(caso.rit_rol || caso.tribunal || caso.numero_juzgado || caso.materia || caso.estado_procesal || caso.fecha_demanda || caso.fecha_notificacion || caso.fecha_audiencia_proxima || caso.cuantia_demanda || caso.domicilio_demandado || caso.representante_legal_demandado || caso.observaciones) && (
          <div className="ls-card p-5 mb-5" data-testid="tribunal-info-card">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-3">
              {caso.rit_rol && <InfoField label="RIT / ROL" value={caso.rit_rol} />}
              {caso.tribunal && <InfoField label="Tribunal" value={TRIBUNAL_LABELS[caso.tribunal] || caso.tribunal} />}
              {caso.numero_juzgado && <InfoField label="Juzgado" value={caso.numero_juzgado} />}
              {caso.materia && <InfoField label="Materia" value={caso.materia} />}
              {caso.estado_procesal && <InfoField label="Estado procesal" value={ESTADO_PROCESAL_LABELS[caso.estado_procesal] || caso.estado_procesal} />}
              {caso.cuantia_demanda > 0 && <InfoField label="Cuantía demanda" value={fmtCLP(caso.cuantia_demanda)} highlight />}
              {caso.fecha_demanda && <InfoField label="Fecha demanda" value={formatDate(caso.fecha_demanda)} />}
              {caso.fecha_notificacion && <InfoField label="Fecha notificación" value={formatDate(caso.fecha_notificacion)} />}
              {caso.fecha_audiencia_proxima && <InfoField label="Próxima audiencia" value={formatDate(caso.fecha_audiencia_proxima)} highlight />}
              {caso.representante_legal_demandado && <InfoField label="Rep. legal demandado" value={caso.representante_legal_demandado} />}
              {caso.domicilio_demandado && <InfoField label="Domicilio demandado" value={caso.domicilio_demandado} />}
            </div>
            {caso.observaciones && (
              <div className="mt-3 pt-3" style={{ borderTop: "1px solid var(--border-light)" }}>
                <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: "var(--text-muted)" }}>Observaciones</p>
                <p className="text-xs" style={{ color: "var(--text-secondary)" }}>{caso.observaciones}</p>
              </div>
            )}
          </div>
        )}

        {/* Valorización + Fases */}
        <div className="grid lg:grid-cols-2 gap-4 mb-5">
          <ValorizacionPanel casoId={id} valorizacion={caso.valorizacion} onUpdate={loadCaso} />
          <FasesProcesales casoId={id} fases={fases} onUpdate={loadCaso} />
        </div>

        {/* Layout de 2 columnas */}
        <div className="grid lg:grid-cols-12 gap-6">
          {/* Columna izquierda - Listas */}
          <div className="lg:col-span-4 space-y-4" data-testid="left-column">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Hechos ({eventos.length})</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="max-h-[60vh] overflow-y-auto" data-testid="hechos-scroll-container">
                  <EventosList
                    eventos={eventos}
                    onSelect={(evento) => {
                      setSelectedEvento(evento);
                      setShowEventoForm(true);
                      setActiveTab("eventos");
                    }}
                    onDelete={handleDeleteEvento}
                  />
                </div>
                <div className="p-4 border-t space-y-2">
                  <Button
                    onClick={() => {
                      setSelectedEvento(null);
                      setShowEventoForm(true);
                      setActiveTab("eventos");
                    }}
                    className="w-full text-white font-semibold"
                    style={{ backgroundColor: '#00E0A8' }}
                    size="sm"
                    data-testid="add-event-btn"
                  >
                    + Agregar Hecho
                  </Button>
                  <Button
                    onClick={handleAbrirImportarModal}
                    className="w-full"
                    size="sm"
                    variant="outline"
                    data-testid="import-events-btn"
                  >
                    <FolderInput className="h-4 w-4 mr-2" />
                    Importar Hechos
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Documentos ({documentos.length})</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <DocumentosList
                  documentos={documentos}
                  casoId={id}
                  onUpdate={loadCaso}
                />
                <div className="p-4 border-t">
                  <Button
                    onClick={() => {
                      setShowDocumentoUpload(true);
                      setActiveTab("documentos");
                    }}
                    className="w-full"
                    size="sm"
                    variant="outline"
                    data-testid="add-document-btn"
                  >
                    + Adjuntar PDF
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Columna derecha - Formularios y Timeline */}
          <div className="lg:col-span-8" data-testid="center-column">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="w-full grid grid-cols-3">
                <TabsTrigger value="timeline" data-testid="tab-timeline">Timeline</TabsTrigger>
                <TabsTrigger value="eventos" data-testid="tab-eventos">Eventos</TabsTrigger>
                <TabsTrigger value="documentos" data-testid="tab-documentos">Documentos</TabsTrigger>
              </TabsList>

              <TabsContent value="timeline" className="mt-4">
                <Card>
                  <CardHeader>
                    <CardTitle>Línea de Tiempo Cronológica</CardTitle>
                    <CardDescription>
                      Visualización cronológica de todos los hechos y documentos del caso
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="max-h-[65vh] overflow-y-auto pr-2" data-testid="timeline-scroll-container">
                      <EventosTimeline
                        eventos={eventos}
                        documentos={documentos}
                        casoId={id}
                        onSelectEvento={(evento) => {
                          setSelectedEvento(evento);
                          setShowEventoForm(true);
                          setActiveTab("eventos");
                        }}
                      />
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="eventos" className="mt-4">
                {showEventoForm ? (
                  <EventoForm
                    evento={selectedEvento}
                    onSave={handleSaveEvento}
                    onCancel={() => {
                      setShowEventoForm(false);
                      setSelectedEvento(null);
                    }}
                  />
                ) : (
                  <Card>
                    <CardContent className="py-16 text-center">
                      <Calendar className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-600">Selecciona un hecho o agrega uno nuevo</p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="documentos" className="mt-4">
                {showDocumentoUpload ? (
                  <DocumentoUpload
                    casoId={id}
                    eventos={eventos}
                    onSuccess={handleDocumentoUploaded}
                    onCancel={() => setShowDocumentoUpload(false)}
                  />
                ) : (
                  <Card>
                    <CardContent className="py-16 text-center">
                      <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-600">Haz clic en "Adjuntar PDF" para subir documentos</p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </main>

      {/* Modal de Confirmar Eliminación de Evento */}
      <Dialog open={eventoToDelete !== null} onOpenChange={(open) => {
        if (!open) {
          setEventoToDelete(null);
          setDeleteEventoPassword("");
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Mover hecho a la papelera</DialogTitle>
            <DialogDescription>
              El hecho se moverá a la papelera (no se borra definitivamente). Ingresa la clave de seguridad para confirmar.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="delete-evento-password">Clave de Seguridad</Label>
            <Input
              id="delete-evento-password"
              type="password"
              placeholder="Ingresa la clave"
              value={deleteEventoPassword}
              onChange={(e) => setDeleteEventoPassword(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  confirmDeleteEvento();
                }
              }}
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setEventoToDelete(null);
                setDeleteEventoPassword("");
              }}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteEvento}
            >
              Eliminar Hecho
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Importar Hechos */}
      <Dialog open={showImportarModal} onOpenChange={setShowImportarModal}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Importar Hechos de Otro Caso</DialogTitle>
            <DialogDescription>
              Selecciona un caso y luego los hechos que deseas copiar. Los documentos adjuntos se importarán automáticamente.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Selector de caso */}
            <div>
              <Label htmlFor="caso-origen">Caso de Origen</Label>
              <Select value={casoOrigenId} onValueChange={handleSeleccionarCasoOrigen}>
                <SelectTrigger id="caso-origen">
                  <SelectValue placeholder="Selecciona un caso..." />
                </SelectTrigger>
                <SelectContent>
                  {casosDisponibles.map(caso => (
                    <SelectItem key={caso.id} value={caso.id}>
                      {caso.titulo_caso} - {caso.empresa_cliente?.nombre_legal || 'N/A'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Lista de hechos */}
            {casoOrigenId && hechosOrigen.length > 0 && (
              <div>
                <Label className="mb-3 block">
                  Hechos Disponibles ({hechosOrigen.length})
                  <span className="text-sm text-gray-500 ml-2">
                    Haz clic para seleccionar
                  </span>
                </Label>
                <div className="border rounded-lg max-h-96 overflow-y-auto">
                  {hechosOrigen.map((hecho) => (
                    <div
                      key={hecho.id}
                      className={`p-4 border-b last:border-b-0 cursor-pointer transition-colors hover:bg-gray-50 ${
                        hechosSeleccionados.includes(hecho.id) ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                      }`}
                      onClick={() => handleToggleHecho(hecho.id)}
                    >
                      <div className="flex items-start gap-3">
                        <Checkbox
                          checked={hechosSeleccionados.includes(hecho.id)}
                          onCheckedChange={() => handleToggleHecho(hecho.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div className="flex-1">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-semibold text-sm">
                                Hecho #{hecho.numero_hecho}: {hecho.titulo_evento}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                {new Date(hecho.fecha_evento).toLocaleDateString('es-CL')} - {hecho.tipo_evento.replace('_', ' ')}
                              </p>
                            </div>
                          </div>
                          <p className="text-sm text-gray-700 mt-2 line-clamp-2">
                            {hecho.relato_usuario}
                          </p>
                          {/* Indicador de documentos adjuntos */}
                          {documentos.filter(d => d.evento_hecho_id === hecho.id).length > 0 && (
                            <div className="flex items-center gap-1 mt-2 text-xs text-blue-600">
                              <FileText className="h-3 w-3" />
                              {documentos.filter(d => d.evento_hecho_id === hecho.id).length} documento(s)
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Resumen de selección */}
                {hechosSeleccionados.length > 0 && (
                  <div className="mt-3 p-3 bg-blue-50 rounded-lg text-sm">
                    <p className="font-medium text-blue-900">
                      {hechosSeleccionados.length} hecho(s) seleccionado(s)
                    </p>
                  </div>
                )}
              </div>
            )}

            {casoOrigenId && hechosOrigen.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>Este caso no tiene hechos para importar</p>
              </div>
            )}

            {/* Opción de renumeración */}
            {hechosSeleccionados.length > 0 && (
              <div className="flex items-center space-x-2 p-4 bg-gray-50 rounded-lg">
                <Checkbox
                  id="renumerar"
                  checked={renumerar}
                  onCheckedChange={setRenumerar}
                />
                <Label htmlFor="renumerar" className="cursor-pointer">
                  Renumerar hechos automáticamente en el caso actual
                </Label>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => {
                setShowImportarModal(false);
                setCasoOrigenId("");
                setHechosOrigen([]);
                setHechosSeleccionados([]);
              }}
              disabled={importando}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleImportarHechos}
              disabled={hechosSeleccionados.length === 0 || importando}
            >
              {importando ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Importando...
                </>
              ) : (
                <>
                  <FolderInput className="h-4 w-4 mr-2" />
                  Importar {hechosSeleccionados.length} Hecho(s)
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Editar Caso Completo */}
      <Dialog open={showEditCaso} onOpenChange={setShowEditCaso}>
        <DialogContent className="bg-white max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Settings className="h-4 w-4" style={{ color: "var(--primary)" }} /> Editar Caso #{caso.numero_caso}</DialogTitle>
            <DialogDescription>Modifica los datos del caso, tribunal, montos y observaciones.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Datos Generales */}
            <div className="text-[10px] uppercase tracking-wider font-medium pb-1" style={{ color: "var(--text-muted)", borderBottom: "1px solid var(--border-light)" }}>Datos Generales</div>
            <div><Label className="text-xs">Título</Label><Input value={editForm.titulo_caso || ""} onChange={(e) => ef("titulo_caso", e.target.value)} data-testid="edit-titulo" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Tipo demanda</Label>
                <Select value={editForm.tipo_demanda || "civil"} onValueChange={(v) => ef("tipo_demanda", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{["civil","laboral","penal","familia","cobranza"].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label className="text-xs">Estado</Label>
                <Select value={editForm.estado_caso || "en_preparacion"} onValueChange={(v) => ef("estado_caso", v)}>
                  <SelectTrigger data-testid="edit-estado"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en_preparacion">En preparación</SelectItem>
                    <SelectItem value="en_revision">En revisión</SelectItem>
                    <SelectItem value="presentado">Presentado</SelectItem>
                    <SelectItem value="cerrado">Cerrado</SelectItem>
                  </SelectContent>
                </Select></div>
            </div>
            <div><Label className="text-xs">Materia</Label><Input value={editForm.materia || ""} onChange={(e) => ef("materia", e.target.value)} /></div>
            <div><Label className="text-xs">Descripción</Label><Textarea value={editForm.descripcion_breve || ""} onChange={(e) => ef("descripcion_breve", e.target.value)} rows={2} /></div>

            {/* Tribunal */}
            <div className="text-[10px] uppercase tracking-wider font-medium pb-1 pt-2" style={{ color: "var(--text-muted)", borderBottom: "1px solid var(--border-light)" }}>Tribunal</div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">RIT / ROL</Label><Input value={editForm.rit_rol || ""} onChange={(e) => ef("rit_rol", e.target.value)} data-testid="edit-rit" /></div>
              <div><Label className="text-xs">Juzgado</Label><Input value={editForm.numero_juzgado || ""} onChange={(e) => ef("numero_juzgado", e.target.value)} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Tribunal</Label>
                <Select value={editForm.tribunal || "sin_definir"} onValueChange={(v) => ef("tribunal", v === "sin_definir" ? "" : v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sin_definir">Sin definir</SelectItem>
                    <SelectItem value="juzgado_civil">Juzgado Civil</SelectItem>
                    <SelectItem value="juzgado_letras">Juzgado de Letras</SelectItem>
                    <SelectItem value="juzgado_laboral">Juzgado de Letras del Trabajo</SelectItem>
                    <SelectItem value="juzgado_familia">Juzgado de Familia</SelectItem>
                    <SelectItem value="corte_apelaciones">Corte de Apelaciones</SelectItem>
                    <SelectItem value="corte_suprema">Corte Suprema</SelectItem>
                  </SelectContent>
                </Select></div>
              <div><Label className="text-xs">Estado procesal</Label>
                <Select value={editForm.estado_procesal || "sin_definir"} onValueChange={(v) => ef("estado_procesal", v === "sin_definir" ? "" : v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sin_definir">Sin definir</SelectItem>
                    <SelectItem value="presentada">Presentada</SelectItem>
                    <SelectItem value="admitida">Admitida</SelectItem>
                    <SelectItem value="notificada">Notificada</SelectItem>
                    <SelectItem value="conciliacion">En conciliación</SelectItem>
                    <SelectItem value="prueba">Periodo de prueba</SelectItem>
                    <SelectItem value="sentencia">En sentencia</SelectItem>
                    <SelectItem value="ejecutoriada">Ejecutoriada</SelectItem>
                  </SelectContent>
                </Select></div>
            </div>
            <div><Label className="text-xs">Cuantía (CLP)</Label><Input value={editForm.cuantia_demanda ? fmtInputCLP(editForm.cuantia_demanda) : ""} onChange={(e) => ef("cuantia_demanda", parseNumCLP(e.target.value))} data-testid="edit-cuantia" /></div>

            {/* Fechas */}
            <div className="text-[10px] uppercase tracking-wider font-medium pb-1 pt-2" style={{ color: "var(--text-muted)", borderBottom: "1px solid var(--border-light)" }}>Fechas</div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label className="text-xs">Fecha demanda</Label><Input type="date" value={editForm.fecha_demanda || ""} onChange={(e) => ef("fecha_demanda", e.target.value)} /></div>
              <div><Label className="text-xs">Notificación</Label><Input type="date" value={editForm.fecha_notificacion || ""} onChange={(e) => ef("fecha_notificacion", e.target.value)} /></div>
              <div><Label className="text-xs">Próxima audiencia</Label><Input type="date" value={editForm.fecha_audiencia_proxima || ""} onChange={(e) => ef("fecha_audiencia_proxima", e.target.value)} /></div>
            </div>

            {/* Demandado */}
            <div className="text-[10px] uppercase tracking-wider font-medium pb-1 pt-2" style={{ color: "var(--text-muted)", borderBottom: "1px solid var(--border-light)" }}>Datos del demandado en el caso</div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Representante legal</Label><Input value={editForm.representante_legal_demandado || ""} onChange={(e) => ef("representante_legal_demandado", e.target.value)} /></div>
              <div><Label className="text-xs">Domicilio</Label><Input value={editForm.domicilio_demandado || ""} onChange={(e) => ef("domicilio_demandado", e.target.value)} /></div>
            </div>

            {isCEO && (
              <>
                {/* Empresa Demandante (Cliente) */}
                <div className="text-[10px] uppercase tracking-wider font-medium pb-1 pt-2" style={{ color: "var(--primary)", borderBottom: "1px solid var(--border-light)" }}>Empresa Demandante (Cliente)</div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Tipo persona</Label>
                    <Select value={editEmpresa.tipo_persona || "juridica"} onValueChange={(v) => efEmp("tipo_persona", v)}>
                      <SelectTrigger data-testid="edit-emp-tipo"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="juridica">Persona Jurídica</SelectItem>
                        <SelectItem value="natural">Persona Natural</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">RUT</Label><Input value={editEmpresa.rut || ""} onChange={(e) => efEmp("rut", e.target.value)} data-testid="edit-emp-rut" /></div>
                </div>
                <div><Label className="text-xs">{editEmpresa.tipo_persona === "natural" ? "Nombre completo" : "Razón social"}</Label><Input value={editEmpresa.nombre_legal || ""} onChange={(e) => efEmp("nombre_legal", e.target.value)} data-testid="edit-emp-nombre" /></div>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label className="text-xs">Contacto principal</Label><Input value={editEmpresa.contacto_principal_nombre || ""} onChange={(e) => efEmp("contacto_principal_nombre", e.target.value)} /></div>
                  <div><Label className="text-xs">Email contacto</Label><Input value={editEmpresa.contacto_principal_email || ""} onChange={(e) => efEmp("contacto_principal_email", e.target.value)} /></div>
                  <div><Label className="text-xs">Teléfono contacto</Label><Input value={editEmpresa.contacto_principal_telefono || ""} onChange={(e) => efEmp("contacto_principal_telefono", e.target.value)} /></div>
                </div>

                {/* Empresa Demandada */}
                <div className="text-[10px] uppercase tracking-wider font-medium pb-1 pt-2" style={{ color: "var(--accent-rose)", borderBottom: "1px solid var(--border-light)" }}>Empresa Demandada</div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Tipo persona</Label>
                    <Select value={editDemandado.tipo_persona || "juridica"} onValueChange={(v) => efDem("tipo_persona", v)}>
                      <SelectTrigger data-testid="edit-dem-tipo"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="juridica">Persona Jurídica</SelectItem>
                        <SelectItem value="natural">Persona Natural</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">RUT</Label><Input value={editDemandado.rut || ""} onChange={(e) => efDem("rut", e.target.value)} data-testid="edit-dem-rut" /></div>
                </div>
                <div><Label className="text-xs">{editDemandado.tipo_persona === "natural" ? "Nombre completo" : "Razón social"}</Label><Input value={editDemandado.nombre_legal || ""} onChange={(e) => efDem("nombre_legal", e.target.value)} data-testid="edit-dem-nombre" /></div>
              </>
            )}

            {/* Valorización */}
            <div className="text-[10px] uppercase tracking-wider font-medium pb-1 pt-2" style={{ color: "var(--text-muted)", borderBottom: "1px solid var(--border-light)" }}>Valorización (CLP)</div>
            <div className="grid grid-cols-3 gap-3">
              {[
                { key: "monto_principal", label: "Principal" },
                { key: "costo_inversion", label: "Inversión" },
                { key: "lucro_cesante", label: "Lucro Cesante" },
                { key: "dano_moral", label: "Daño Moral" },
                { key: "reajuste_ipc", label: "Reajuste IPC" },
                { key: "costas_procesales", label: "Costas" },
              ].map(m => (
                <div key={m.key}>
                  <Label className="text-xs">{m.label}</Label>
                  <Input value={editForm.valorizacion?.[m.key] ? fmtInputCLP(editForm.valorizacion[m.key]) : ""} onChange={(e) => efVal(m.key, e.target.value)} />
                </div>
              ))}
            </div>

            {/* Observaciones */}
            <div className="text-[10px] uppercase tracking-wider font-medium pb-1 pt-2" style={{ color: "var(--text-muted)", borderBottom: "1px solid var(--border-light)" }}>Observaciones</div>
            <Textarea value={editForm.observaciones || ""} onChange={(e) => ef("observaciones", e.target.value)} rows={2} />

            <div className="flex gap-3 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setShowEditCaso(false)}>Cancelar</Button>
              <Button className="flex-1 ls-btn-primary" onClick={saveEditCaso} disabled={savingEdit} data-testid="save-edit-caso-btn">
                {savingEdit ? "Guardando..." : "Guardar Cambios"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default CasoDetalle;
